import { getDocumentBillingDelta } from './saleBillingUtils';

export const BILLING_DOC_TYPE_OPTIONS = [
  { key: 'factura', label: 'Factura', documentType: 'Factura electrónica' },
  { key: 'boleta', label: 'Boleta', documentType: 'Boleta electrónica' },
  { key: 'credito', label: 'Nota de crédito', documentType: 'Nota de crédito electrónica' },
  { key: 'debito', label: 'Nota de débito', documentType: 'Nota de débito electrónica' },
];

export const REGISTER_BILLING_OPTION = {
  key: 'register',
  label: 'Desde libro contable',
};

const normalizeDocTypeKey = (value = '') =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');

export const isCreditBillingDocumentType = (documentType = '') =>
  normalizeDocTypeKey(documentType).includes('credito');

export const getBillingDocumentAmount = (document = {}) => {
  const net = Number(document?.net_amount ?? 0);
  const total = Number(document?.total_amount ?? 0);
  const magnitude = Math.abs(net) > 1e-9 ? Math.abs(net) : Math.abs(total);
  if (!magnitude) return 0;
  if (isCreditBillingDocumentType(document?.document_type) || net < 0 || total < 0) {
    return -magnitude;
  }
  return magnitude;
};

export const amountsClose = (left, right, tolerance = 1) =>
  Math.abs(Number(left || 0) - Number(right || 0)) <= tolerance;

export const quotationGrossAmount = (netTotal) => {
  const net = Number(netTotal || 0);
  if (!Number.isFinite(net) || net === 0) return 0;
  return Math.round(net * 1.19 * 100) / 100;
};

export const documentAmountMatchesSaleTotal = (documentAmount, saleNetTotal) => {
  const amount = Math.abs(Number(documentAmount || 0));
  const targetNet = Math.abs(Number(saleNetTotal || 0));
  const targetGross = quotationGrossAmount(targetNet);
  return amountsClose(amount, targetNet) || amountsClose(amount, targetGross);
};

export const getAllSaleItemIds = (items = []) =>
  (items || [])
    .map((item) => Number(item?.id || 0))
    .filter((id) => id > 0);

export const getAllSaleItemSelection = (items = []) =>
  Object.fromEntries(
    (items || [])
      .map((item) => [Number(item.id), Number(item.quantity || 0)])
      .filter(([id, qty]) => id > 0 && qty > 0)
  );

const clampQuantity = (value, maxQty) => {
  const max = Number(maxQty || 0);
  const qty = Number(value || 0);
  if (!max || qty <= 0) return 0;
  return Math.min(max, Math.max(0, qty));
};

export const normalizeBillingSelection = (selection = {}, items = []) => {
  const itemsById = Object.fromEntries((items || []).map((item) => [Number(item.id), item]));

  if (Array.isArray(selection)) {
    if (selection.length && typeof selection[0] === 'object') {
      return selection
        .map((entry) => {
          const id = Number(entry?.id || entry?.item_id || 0);
          const item = itemsById[id];
          const maxQty = Number(item?.quantity || entry?.max_quantity || 0);
          const qty = clampQuantity(entry?.quantity, maxQty);
          return qty > 0 ? { id, quantity: qty } : null;
        })
        .filter(Boolean);
    }
    return selection
      .map((id) => Number(id))
      .filter((id) => id > 0)
      .map((id) => {
        const item = itemsById[id];
        const qty = Number(item?.quantity || 0);
        return qty > 0 ? { id, quantity: qty } : null;
      })
      .filter(Boolean);
  }

  return Object.entries(selection || {})
    .map(([rawId, rawQty]) => {
      const id = Number(rawId);
      const item = itemsById[id];
      const maxQty = Number(item?.quantity || 0);
      const qty = clampQuantity(rawQty, maxQty);
      return qty > 0 ? { id, quantity: qty } : null;
    })
    .filter(Boolean);
};

export const billingSelectionToMap = (selection = {}, items = []) =>
  Object.fromEntries(
    normalizeBillingSelection(selection, items).map((entry) => [entry.id, entry.quantity])
  );

export const billingSelectionToIds = (selection = {}, items = []) =>
  normalizeBillingSelection(selection, items).map((entry) => entry.id);

export const hasBillingSelection = (selection = {}, items = []) =>
  normalizeBillingSelection(selection, items).length > 0;

export const getItemLineNetForQuantity = (item = {}, quantity) => {
  const lineQty = Number(item.quantity || 0);
  const lineTotal = Number(item.line_total || 0);
  const qty = Number(quantity || 0);
  if (!qty || !lineQty) return 0;
  if (qty >= lineQty) return lineTotal;
  return Math.round((lineTotal / lineQty) * qty * 100) / 100;
};

export const getItemLineGrossForQuantity = (item = {}, quantity) =>
  quotationGrossAmount(getItemLineNetForQuantity(item, quantity));

export const sumSelectedItemsNet = (items = [], selection = {}, documentType = '') => {
  const entries = normalizeBillingSelection(selection, items);
  const total = entries.reduce((acc, entry) => {
    const item = (items || []).find((row) => Number(row.id) === Number(entry.id));
    if (!item) return acc;
    return acc + getItemLineNetForQuantity(item, entry.quantity);
  }, 0);
  if (isCreditBillingDocumentType(documentType)) {
    return -Math.abs(total);
  }
  return total;
};

export const sumSelectedItemsGross = (items = [], selection = {}, documentType = '') => {
  const net = sumSelectedItemsNet(items, selection, documentType);
  if (!net) return 0;
  const gross = quotationGrossAmount(Math.abs(net));
  return net < 0 ? -gross : gross;
};

export const buildLinkItemNotes = (selection = [], isReplacement = false, items = []) => {
  const entries = normalizeBillingSelection(selection, items);
  const itemQuantities = {};
  entries.forEach((entry) => {
    itemQuantities[String(entry.id)] = entry.quantity;
  });
  return JSON.stringify({
    quotation_item_ids: entries.map((entry) => entry.id),
    item_quantities: itemQuantities,
    is_replacement: Boolean(isReplacement),
  });
};

export const parseLinkNotes = (notes = '') => {
  const raw = String(notes || '').trim();
  if (!raw) return { quotation_item_ids: [], item_quantities: {}, is_replacement: false };
  try {
    const payload = JSON.parse(raw);
    const ids = payload?.quotation_item_ids || payload?.item_ids || [];
    const quantities = payload?.item_quantities || {};
    const itemQuantities = {};
    if (quantities && typeof quantities === 'object') {
      Object.entries(quantities).forEach(([key, value]) => {
        const qty = Number(value);
        if (qty > 0) itemQuantities[String(key)] = qty;
      });
    }
    return {
      quotation_item_ids: Array.isArray(ids)
        ? ids.map((id) => Number(id)).filter((id) => id > 0)
        : [],
      item_quantities: itemQuantities,
      is_replacement: Boolean(payload?.is_replacement),
    };
  } catch {
    return { quotation_item_ids: [], item_quantities: {}, is_replacement: false };
  }
};

export const parseLinkItemIds = (notes = '') => parseLinkNotes(notes).quotation_item_ids;

export const mergeBillingDocuments = (linkedDocuments = [], manualDocuments = []) => [
  ...(linkedDocuments || []).map((doc) => ({ ...doc, source: doc.source || 'register' })),
  ...(manualDocuments || []).map((doc) => ({ ...doc, source: doc.source || 'manual' })),
];

export const formatBillingItemsSummary = (items = [], invoicedItemIds = [], itemQuantities = {}) => {
  const selected = new Set((invoicedItemIds || []).map((id) => Number(id)));
  const count = selected.size;
  if (!count) return 'Sin ítems';
  const labels = (items || [])
    .filter((item) => selected.has(Number(item.id)))
    .map((item) => {
      const name = String(item.item_name || item.product_sku || item.item_code || '').trim();
      const qty = Number(itemQuantities[String(item.id)] ?? itemQuantities[item.id] ?? item.quantity ?? 0);
      const unit = String(item.unit || '').trim();
      if (!name) return '';
      if (qty > 0 && unit) return `${name} (${qty} ${unit})`;
      return name;
    })
    .filter(Boolean)
    .slice(0, 2);
  const suffix = count > 2 ? ` +${count - 2}` : '';
  return labels.length ? `${labels.join(', ')}${suffix}` : `${count} ítem${count === 1 ? '' : 's'}`;
};

export const getBillingDocumentDisplayAmount = (document = {}) =>
  getDocumentBillingDelta(document);

export const getClaimedBillingItemQuantities = (billingDocuments = [], items = []) => {
  const itemsById = Object.fromEntries((items || []).map((item) => [Number(item.id), item]));
  const claimed = {};
  (billingDocuments || []).forEach((doc) => {
    if (doc?.is_replacement) return;
    const quantities = doc.invoiced_item_quantities || doc.item_quantities || {};
    (doc.invoiced_item_ids || []).forEach((rawId) => {
      const id = Number(rawId);
      if (!id) return;
      const fallbackQty = Number(itemsById[id]?.quantity || 0);
      const qty = Number(quantities[String(id)] ?? quantities[id] ?? fallbackQty);
      if (qty > 0) claimed[id] = (claimed[id] || 0) + qty;
    });
  });
  return claimed;
};

export const getClaimedBillingItemIds = (billingDocuments = []) => {
  const claimed = new Set();
  Object.keys(getClaimedBillingItemQuantities(billingDocuments)).forEach((id) => {
    if (Number(id) > 0) claimed.add(Number(id));
  });
  return claimed;
};

export const getAvailableItemQuantity = (item = {}, claimedQuantities = {}) => {
  const total = Number(item?.quantity || 0);
  const claimed = Number(claimedQuantities[Number(item?.id)] || 0);
  return Math.max(0, total - claimed);
};

export const getSelectableBillingItems = (items = [], claimedQuantities = {}, isReplacement = false) => {
  if (isReplacement) return items || [];
  return (items || []).filter((item) => getAvailableItemQuantity(item, claimedQuantities) > 0.0001);
};

export const formatItemQuantity = (item = {}, quantity = null) => {
  const qty = quantity == null ? Number(item.quantity || 0) : Number(quantity || 0);
  const unit = String(item.unit || '').trim();
  if (!qty) return '—';
  return unit ? `${qty.toLocaleString('es-CL')} ${unit}` : qty.toLocaleString('es-CL');
};
